import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { AutosaveIndicator } from './studio/AutosaveIndicator';
import type { TrustAutosaveStatus } from '../../lib/trust/trustAutosaveTypes';

export type GradingActionStripProps = {
  onSaveDraft: () => void;
  onSaveAndNext: () => void;
  onFinalize: () => void;
  onPrevious?: () => void;
  previousDisabled?: boolean;
  saveNextDisabled?: boolean;
  disabled?: boolean;
  autosaveStatus: TrustAutosaveStatus;
  className?: string;
};

export function GradingActionStrip({
  onSaveDraft,
  onSaveAndNext,
  onFinalize,
  onPrevious,
  previousDisabled = false,
  saveNextDisabled = false,
  disabled = false,
  autosaveStatus,
  className,
}: GradingActionStripProps): ReactElement {
  const { t } = useTranslation();

  return (
    <footer
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 border-t border-dg-border bg-dg-bg-surface/95 shadow-[0_-6px_28px_rgba(15,23,42,0.08)] backdrop-blur-md dark:border-slate-700/70 dark:bg-slate-900/95 dark:shadow-[0_-6px_28px_rgba(0,0,0,0.4)]',
        'pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:static lg:z-auto lg:shrink-0 lg:shadow-none',
        className
      )}
      data-dg-grading-action-strip
    >
      <AutosaveIndicator status={autosaveStatus} variant="bar" />
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
        <AutosaveIndicator status={autosaveStatus} className="hidden min-w-0 sm:inline-flex" />
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          {onPrevious ? (
            <button
              type="button"
              disabled={disabled || previousDisabled}
              onClick={onPrevious}
              className="hidden h-11 shrink-0 rounded-dg-control border border-slate-200/90 bg-white px-4 text-dg-button font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 lg:inline-flex"
            >
              {t('audit.gradeStudio.previous')}
            </button>
          ) : null}
          <button
            type="button"
            disabled={disabled}
            onClick={onSaveDraft}
            className="h-11 min-w-0 flex-1 rounded-dg-control border border-slate-200/90 bg-white text-dg-button font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 sm:max-w-[9rem]"
          >
            {t('audit.gradeStudio.saveDraft')}
          </button>
          <button
            type="button"
            disabled={disabled || saveNextDisabled}
            onClick={onSaveAndNext}
            data-dg-primary-grade-action
            className="h-11 min-w-0 flex-[1.25] rounded-dg-control bg-dg-accent-grade text-dg-button font-semibold text-white shadow-sm transition-transform hover:brightness-105 active:scale-[0.99] disabled:opacity-50"
          >
            {t('audit.gradeStudio.saveNext')}
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={onFinalize}
            className="hidden h-11 shrink-0 rounded-dg-control border border-emerald-500/35 bg-emerald-50 px-4 text-dg-button font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700/40 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-950/60 sm:inline-flex lg:max-w-[8.5rem]"
          >
            {t('audit.gradeStudio.finalize')}
          </button>
        </div>
      </div>
    </footer>
  );
}
